/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Inject, Injectable, Optional} from '@angular/core';


import {Location} from './location';
import {APP_BASE_HREF, LocationStrategy} from './location_strategy';
import {LocationChangeListener, PlatformLocation} from './platform_location';
/**
 * \@whatItDoes Use URL hash for storing application location data.
 * \@description 
 * `HashLocationStrategy` is a {\@link LocationStrategy} used to configure the
 * {\@link Location} service to represent its state in the
 * [hash fragment](https://en.wikipedia.org/wiki/Uniform_Resource_Locator#Syntax)
 * of the browser's URL.
 * 
 * For instance, if you call `location.go('/foo')`, the browser's URL will become
 * `example.com#/foo`.
 * 
 * ### Example
 * 
 * {\@example common/location/ts/hash_location_component.ts region='LocationComponent'}
 * 
 * \@stable
 */
export class HashLocationStrategy extends LocationStrategy {
private _baseHref: string = '';
/**
 * @param {?} _platformLocation
 * @param {?=} _baseHref
 */
constructor(
private _platformLocation: PlatformLocation,
        _baseHref?: string) {
    super();
    if (_baseHref != null) {
      this._baseHref = _baseHref;
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
onPopState(fn: LocationChangeListener): void {
    this._platformLocation.onPopState(fn);
    this._platformLocation.onHashChange(fn);
  }
/**
 * @return {?}
 */
getBaseHref(): string { return this._baseHref; }
/**
 * @param {?=} includeHash
 * @return {?}
 */
path(includeHash: boolean = false): string {
    // the hash value is always prefixed with a `#`
    // and if it is empty then it will stay empty
    let /** @type {?} */ path = this._platformLocation.hash;
    if (path == null) path = '#';

    return path.length > 0 ? path.substring(1) : path;
  }
/**
 * @param {?} internal
 * @return {?}
 */
prepareExternalUrl(internal: string): string {
    const /** @type {?} */ url = Location.joinWithSlash(this._baseHref, internal);
    return url.length > 0 ? ('#' + url) : url;
  }
/**
 * @param {?} state
 * @param {?} title
 * @param {?} path
 * @param {?} queryParams
 * @return {?}
 */
pushState(state: any, title: string, path: string, queryParams: string) {
    let /** @type {?} */ url: string|null =
        this.prepareExternalUrl(path + Location.normalizeQueryParams(queryParams));
    if (url.length == 0) {
      url = this._platformLocation.pathname;
    }
    this._platformLocation.pushState(state, title, url);
  }
/**
 * @param {?} state
 * @param {?} title
 * @param {?} path
 * @param {?} queryParams
 * @return {?}
 */
replaceState(state: any, title: string, path: string, queryParams: string) {
    let /** @type {?} */ url = this.prepareExternalUrl(path + Location.normalizeQueryParams(queryParams));
    if (url.length == 0) {
      url = this._platformLocation.pathname;
    }
    this._platformLocation.replaceState(state, title, url);
  }
/**
 * @return {?}
 */
forward(): void { this._platformLocation.forward(); }
/**
 * @return {?}
 */
back(): void { this._platformLocation.back(); }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: PlatformLocation, },
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [APP_BASE_HREF, ] }, ]},
];
}

function HashLocationStrategy_tsickle_Closure_declarations() {
/** @type {?} */
HashLocationStrategy.decorators;
/**
 * @nocollapse
 * @type {?}
 */
HashLocationStrategy.ctorParameters;
/** @type {?} */
HashLocationStrategy.prototype._baseHref;
/** @type {?} */
HashLocationStrategy.prototype._platformLocation;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../../packages/common/src/location/hash_location_strategy.ts